import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Search, User } from "lucide-react";
import useCrmApi from "../../../../components/hook/crm-req";
import { ArchivedConversation, formatArchiveDate } from "./types";
import ConversationPreviewModal from "./conversation-preview-modal";

const PAGE_SIZE = 20;

const STATUS_LABEL: Record<string, string> = {
  open: "Open",
  closed: "Closed",
};

const STATUS_STYLE: Record<string, string> = {
  open: "bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400",
  closed: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span
    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
      STATUS_STYLE[status] || "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
    }`}
  >
    {STATUS_LABEL[status] || status}
  </span>
);

const ArchivedRow: React.FC<{
  conversation: ArchivedConversation;
  onOpen: () => void;
}> = ({ conversation, onOpen }) => (
  <tr onClick={onOpen} className="border-b border-gray-50 dark:border-gray-800 hover:bg-indigo-50/30 dark:hover:bg-indigo-500/10 transition-colors cursor-pointer group">
    <td className="px-4 py-4">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-indigo-100 dark:bg-indigo-500/15 flex items-center justify-center shrink-0">
          <User size={17} className="text-indigo-500 dark:text-indigo-400" />
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{conversation.contact?.name || "—"}</p>
          <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">{conversation.contact?.phone || "—"}</p>
        </div>
      </div>
    </td>
    <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300 max-w-[200px] truncate" title={conversation.contact?.email}>
      {conversation.contact?.email || "—"}
    </td>
    <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">{conversation.recent_message || "—"}</td>
    <td className="px-4 py-4 text-sm font-medium text-gray-800 dark:text-gray-100">{formatArchiveDate(conversation.start_date)}</td>
    <td className="px-4 py-4 text-sm font-medium text-gray-800 dark:text-gray-100">{formatArchiveDate(conversation.end_date)}</td>
    <td className="px-4 py-4">
      <StatusBadge status={conversation.status} />
    </td>
    <td className="px-4 py-4 text-sm text-gray-800 dark:text-gray-100">{conversation.assigned_by || "—"}</td>
  </tr>
);

const ArchivedChat: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState<ArchivedConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewConversation, setPreviewConversation] = useState<ArchivedConversation | null>(null);
  const [resuming, setResuming] = useState(false);

  const { makeRequest: getArchivedConversations } = useCrmApi(`/conversations/archive`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  // useCrmApi binds the endpoint at hook-call time, so re-create it whenever
  // the conversation being restored changes — mirrors the takeover/handoff
  // pattern used in chatarea.tsx.
  const { makeRequest: unarchiveConversation } = useCrmApi(
    `/conversations/${previewConversation?.id}/unarchive`,
    "PATCH",
    { Authorization: `Bearer ${token}` }
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setOffset(0);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    const fetchArchived = async () => {
      setLoading(true);
      try {
        const params: Record<string, any> = { limit: PAGE_SIZE, offset };
        if (debouncedSearch) params.search = debouncedSearch;
        const [response] = await getArchivedConversations(undefined, params);
        setRows(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error("Error fetching archived conversations:", error);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    fetchArchived();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, offset]);

  // The archive API returns a bare array with no total count, so pagination
  // is Prev/Next only — a full page implies there may be more.
  const hasNextPage = rows.length === PAGE_SIZE;
  const hasPrevPage = offset > 0;

  // Resuming an archived conversation means restoring it first (per the API
  // docs' integration notes: "Use PATCH .../unarchive for restore actions
  // from the archive screen"), then jumping to the inbox and selecting it —
  // same sessionStorage bridge the notification toast's "View" action uses,
  // now keyed on the real conversation id instead of a fuzzy name/phone match.
  const handleResumeConversation = async (conversation: ArchivedConversation) => {
    setResuming(true);
    try {
      const [, status] = await unarchiveConversation();
      if (status < 200 || status >= 300) {
        toast.error("Failed to restore conversation");
        return;
      }
      sessionStorage.setItem(
        "pendingConversationTarget",
        JSON.stringify({
          conversationId: conversation.id,
          phoneNumber: conversation.contact?.phone,
          title: conversation.contact?.name,
        })
      );
      setPreviewConversation(null);
      navigate("/chats");
    } catch (error) {
      console.error("Error restoring conversation:", error);
      toast.error("Failed to restore conversation");
    } finally {
      setResuming(false);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Conversation Archive</h1>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex-1 min-w-[180px] relative">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500" />
          <input
            type="text"
            placeholder="Search by name, phone, or thread id"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full sm:w-80 pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-500/30 bg-white dark:bg-gray-800 dark:text-gray-100"
          />
        </div>
      </div>

      <div className="hidden md:block bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100 dark:border-gray-800">
              <th className="text-left px-4 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide">Contact</th>
              <th className="text-left px-4 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide">Email Address</th>
              <th className="text-left px-4 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide">Recent Message</th>
              <th className="text-left px-4 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide">Start Date</th>
              <th className="text-left px-4 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide">End Date</th>
              <th className="text-left px-4 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide">Status</th>
              <th className="text-left px-4 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide">Assigned by</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-[#7B7B7B] font-semibold dark:text-gray-500 text-sm">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-[#7B7B7B] font-semibold dark:text-gray-500 text-sm">
                  No archived conversations found.
                </td>
              </tr>
            ) : (
              rows.map((c) => <ArchivedRow key={c.id} conversation={c} onOpen={() => setPreviewConversation(c)} />)
            )}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-3">
        {loading ? (
          <p className="text-center text-[#7B7B7B] font-semibold dark:text-gray-500 text-sm py-12">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-center text-[#7B7B7B] font-semibold dark:text-gray-500 text-sm py-12">No archived conversations found.</p>
        ) : (
          rows.map((c) => (
            <div
              key={c.id}
              onClick={() => setPreviewConversation(c)}
              className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-4 cursor-pointer hover:border-indigo-200 dark:hover:border-indigo-500/40 transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-500/15 flex items-center justify-center shrink-0">
                  <User size={18} className="text-indigo-500 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{c.contact?.name || "—"}</p>
                    <StatusBadge status={c.status} />
                  </div>
                  <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-0.5">{c.contact?.phone || "—"}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">{c.contact?.email || "—"}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">{c.recent_message || "—"}</p>
                  <div className="flex items-center justify-between mt-2 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">
                    <span>{formatArchiveDate(c.start_date)}</span>
                    <span>Assigned: {c.assigned_by || "—"}</span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="flex items-center justify-end gap-2 mt-4">
        <button
          onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
          disabled={!hasPrevPage || loading}
          className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={15} /> Prev
        </button>
        <button
          onClick={() => setOffset((o) => o + PAGE_SIZE)}
          disabled={!hasNextPage || loading}
          className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Next <ChevronRight size={15} />
        </button>
      </div>

      <ConversationPreviewModal
        conversation={previewConversation}
        onClose={() => setPreviewConversation(null)}
        onResume={handleResumeConversation}
        resuming={resuming}
      />
    </div>
  );
};

export default ArchivedChat;
